/**
 * auditspine.mjs — a learner audits a real ANKR product, and a negative finding is a pass.
 *
 * FOUNDER, 2026-08-15: "all ai courses were supposed to have a user own project along with
 * evaluating ankr products" → "build p2 and p4".
 *
 * ── THE GRADING LAW THAT MAKES THIS HONEST OR WORTHLESS ───────────────────────────────────────
 * If a learner audits an ANKR product and reports that it FAILS a Forja element, that is a PASS.
 *
 * Everything else in this module follows from that sentence. An exercise where the good answer is
 * the favourable one teaches flattery, and it would do so inside the course that teaches
 * declaration over inference. So the check tests that all four declarations were EXAMINED and each
 * finding carries a REASON. It does not test whether the finding is favourable, and it does not
 * compare the learner's answer to ANKR's own.
 *
 * ── DISAGREEING WITH US IS ALLOWED AND IS NOT PENALISED ───────────────────────────────────────
 * ANKR's own finding on each subject is published so a learner can compare AFTERWARDS. It is not
 * an answer key and nothing scores against it. A learner who finds something we missed has done
 * the exercise better than we did, and the code has no way to mark that down.
 *
 * ── WHICH PRODUCTS ARE AUDITABLE IS NOT AN ENGINEERING DECISION ───────────────────────────────
 * The subject list is a founder disclosure ruling. This module reads it and adds nothing to it.
 */

/** §1 of CodeAI101 module 6 — the four, in his order and his words. */
export const DECLARATIONS = Object.freeze([
  { id: 'state', name: 'STATE', asks: 'what data and configuration it holds' },
  { id: 'trust', name: 'TRUST', asks: 'what operations it is reliable enough to perform, with an honest level per operation' },
  { id: 'sense', name: 'SENSE', asks: 'what events it emits while doing its work' },
  { id: 'proof', name: 'PROOF', asks: 'what evidence it produces alongside any answer' },
]);

/** A finding is one of these. Deliberately NOT a scale — a scale invites averaging. */
export const VERDICTS = Object.freeze([
  { id: 'declared', label: 'Declared', means: 'the product states this, in a form I could read' },
  { id: 'partial', label: 'Partially declared', means: 'something is stated, but not completely or not per-operation' },
  { id: 'absent', label: 'Not declared', means: 'I looked and found nothing stating this' },
  { id: 'unreadable', label: 'Could not tell', means: 'I could not reach enough of it to say — an honest abstention' },
]);

export const subjectFor = (decl, id) => (decl?.subjects || []).find((s) => s.id === id) || null;
export const subjects = (decl) => [...(decl?.subjects || [])];

/**
 * THE CHECK. Published before the learner starts, like every check in this academy.
 *
 * It asks two things of each of the four declarations: was it EXAMINED, and does the finding carry
 * a REASON. Nothing else. In particular it never reads which verdict was chosen, so there is no
 * path through this function by which a favourable finding scores better than an unfavourable one.
 */
export function checkAudit(audit = {}) {
  const findings = audit.findings || {};
  const examined = DECLARATIONS.filter((d) => {
    const f = findings[d.id];
    return f && VERDICTS.some((v) => v.id === f.verdict);
  });
  const reasoned = examined.filter((d) => {
    const why = findings[d.id]?.why;
    return typeof why === 'string' && why.trim().length >= 15;
  });
  const missing = DECLARATIONS.filter((d) => !examined.includes(d));
  const unreasoned = examined.filter((d) => !reasoned.includes(d));
  return {
    published_standard: DECLARATIONS.map((d) => ({
      id: d.id, asks: `examine ${d.name} — ${d.asks} — and say why you concluded what you did`,
    })),
    passes: missing.length === 0 && unreasoned.length === 0,
    missing: missing.map((d) => ({ id: d.id, asks: `${d.name} was not examined` })),
    unreasoned: unreasoned.map((d) => ({ id: d.id, asks: `${d.name} has a verdict but no reason` })),
    verdict: missing.length === 0 && unreasoned.length === 0
      ? 'all four declarations examined, each with a reason'
      : `${missing.length + unreasoned.length} of ${DECLARATIONS.length} still to do`,
    /* Reported so a learner sees their own shape, NEVER used in passes. A four-absent audit and a
       four-declared audit are equally valid outcomes of the same exercise. */
    shape: Object.fromEntries(DECLARATIONS.map((d) => [d.id, findings[d.id]?.verdict ?? null])),
  };
}

/**
 * Compare a learner's finding with ANKR's own — AFTER they have made theirs.
 *
 * Returns agreement per declaration and nothing resembling a mark. `differs` is presented as
 * something to look into, in either direction: a learner who found a gap we did not has found a
 * gap, and this function is careful never to describe that as being wrong.
 */
export function compareWithOurs(subject, audit = {}) {
  const ours = subject?.our_own_finding || {};
  return DECLARATIONS.map((d) => {
    const mine = audit.findings?.[d.id]?.verdict ?? null;
    const theirs = ours[d.id] ?? null;
    return {
      id: d.id, name: d.name, mine, ours: theirs,
      differs: Boolean(mine && theirs && !String(theirs).toLowerCase().startsWith(String(mine).toLowerCase().slice(0, 6))),
      note: 'A difference is a thing to look into, not a mistake. Ours is published so you can compare, never to mark against.',
    };
  });
}

export const NEGATIVE_IS_A_PASS = 'Reporting that a product FAILS a declaration is a pass. The check tests that you examined all four and said why — not whether what you found was favourable, and not whether it matches ours.';
export const NOT_AN_ANSWER_KEY = 'ANKR\'s own finding is published so you can compare after you have made yours. Nothing scores against it. A learner who finds something we missed has done this better than we did.';
export const WHO_DECIDES_SUBJECTS = 'Which products appear here is a founder disclosure decision, not an engineering one. No process, script or assistant may add a subject.';
